import { Injectable, inject } from '@angular/core';
import {Router} from '@angular/router';
import {AuthConfig, OAuthService} from 'angular-oauth2-oidc';
import {BehaviorSubject, Observable} from 'rxjs';
import {filter} from 'rxjs/operators';
import {Browser} from '@capacitor/browser';
import {CapacitorHttp, HttpOptions, HttpResponse} from '@capacitor/core';

import {PlatformService} from './platform.service';
import { AuthSession, isAuthSessionUnavailable } from './auth-session.plugin';
import {TokenStorageService} from './token-storage.service';
import {safeReturnUrl} from '../return-url';
import {BelgianClaims, UserProfile} from '../models/user-profile.model';
import {APP_CONFIG} from '../../config/app-config.token';
import {AppConfig} from '../../config/app-config.model';

/** Keycloak token endpoint response (RFC 6749 §5.1) */
interface TokenResponse {
  access_token?: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
  scope?: string | string[];
}

/** ID token claims as decoded from the JWT / returned by getIdentityClaims() */
interface IdTokenClaims {
  sub?: string;
  name?: string;
  preferred_username?: string;
  azp?: string;
  'be:abvv-fgtb:ssin'?: string;
  'be:abvv-fgtb:display_name'?: string;
  'be:abvv-fgtb:person_type'?: string;
  'be:abvv-fgtb:user_id'?: string;
  [key: string]: unknown;
}

/**
 * AuthService
 *
 * Main authentication service implementing OAuth2/OIDC authentication flow
 * with Keycloak. Handles:
 * - OAuth2 Authorization Code Flow with PKCE
 * - Token management (access, refresh, ID tokens)
 * - Belgian FAS claim parsing
 * - Platform-specific redirect URIs (mobile vs web)
 * - Authentication state management with RxJS observables
 *
 * This service replaces the legacy keycloak-angular implementation
 * and provides mobile-compatible authentication.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private oauthService = inject(OAuthService);
  private platformService = inject(PlatformService);
  private tokenStorage = inject(TokenStorageService);
  private router = inject(Router);
  private config = inject<AppConfig>(APP_CONFIG);

  /**
   * Base URL of this environment's Keycloak realm.
   *
   * The realm was hardcoded to 'MyABVV' in six places until 2026-10-09. It is
   * per-environment data — myabvv-new.abvv.be runs against MyABVVNew — and it
   * stopped being configurable when 9568a752 removed environment.newExt.ts
   * (which carried it in tokenEndpoint/iframeUrl) without adding it to
   * AppConfig. Derive every realm-scoped URL from here so the two cannot drift.
   */
  private get realmUrl(): string {
    return `${this.config.keycloakRootUrl}/realms/${this.config.keycloakRealm}`;
  }

  // Private state subjects
  private _isAuthenticated$ = new BehaviorSubject<boolean>(false);
  private _userProfile$ = new BehaviorSubject<UserProfile | null>(null);
  private _intendedUrl: string | null = null;

  // SECURITY FIX (MEDIUM-02): Rate limiting for login attempts
  private loginAttempts = 0;
  private lastLoginAttempt = 0;
  private loginAttemptsCooldown = 0;
  private readonly MAX_LOGIN_ATTEMPTS = 5;
  private readonly COOLDOWN_PERIOD_MS = 60000; // 1 minute
  private readonly MIN_LOGIN_INTERVAL_MS = 500; // 500ms between attempts

  // Proactive token refresh timer (for ROPC tokens that oauthService doesn't manage)
  private tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  // Track if current session is from ROPC test login (skip server-side logout)
  private isTestSession = false;

  // Public observables for components to subscribe to
  public readonly isAuthenticated$: Observable<boolean>;
  public readonly userProfile$: Observable<UserProfile | null>;

  constructor() {
    this.isAuthenticated$ = this._isAuthenticated$.asObservable();
    this.userProfile$ = this._userProfile$.asObservable();
  }

  /**
   * Initialize authentication system
   *
   * Configures OAuth2 settings, loads discovery document,
   * restores stored tokens, and sets up token event listeners.
   *
   * Call this in APP_INITIALIZER or component ngOnInit.
   */
  async initAuth(): Promise<void> {
    this.configureOAuth();

    try {
      // Load OIDC discovery document
      await this.oauthService.loadDiscoveryDocument();

      // Restore tokens from secure storage
      await this.loadStoredTokens();

      // Check if we have valid tokens
      if (this.oauthService.hasValidAccessToken()) {
        await this.updateAuthState();
        // Schedule proactive refresh for restored tokens
        this.scheduleTokenRefresh();
      } else {
        // Tokens expired or missing — check if we can refresh
        const refreshToken = sessionStorage.getItem('refresh_token');
        if (refreshToken) {
          const refreshed = await this.refreshToken();
          if (refreshed) {
            await this.updateAuthState();
          } else {
            console.warn('[AuthService] Token refresh failed, clearing stale session');
            await this.clearLocalSession();
          }
        } else {
          // No tokens at all — clean slate
          await this.clearLocalSession();
        }
      }

      // Listen for token events
      this.setupTokenEventListeners();

    } catch (error) {
      console.error('[AuthService] Auth initialization failed:', error);
      throw error;
    }
  }

  /**
   * Clear all local auth state without redirecting.
   * Used when stored tokens are expired and can't be refreshed.
   * @private
   */
  /**
   * Terminate the Keycloak server-side session via backchannel POST.
   * Uses the refresh token to identify the session to revoke.
   * Done via CapacitorHttp to bypass CORS on mobile.
   * @private
   */
  private async backchannelLogout(): Promise<void> {
    const refreshToken = sessionStorage.getItem('refresh_token');
    if (!refreshToken) return;

    const logoutEndpoint = `${this.realmUrl}/protocol/openid-connect/logout`;
    const body = new URLSearchParams({
      client_id: this.config.keycloakContext,
      refresh_token: refreshToken
    });

    try {
      await CapacitorHttp.post({
        url: logoutEndpoint,
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data: body.toString()
      });
    } catch (error) {
      // Non-critical — local logout still succeeds
      console.warn('[AuthService] Backchannel logout failed (session may persist):', error);
    }
  }

  private async clearLocalSession(): Promise<void> {
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('expires_at');
    sessionStorage.removeItem('granted_scopes');
    sessionStorage.removeItem('id_token_claims_obj');
    await this.tokenStorage.clearAll();
    this._isAuthenticated$.next(false);
    this._userProfile$.next(null);
  }

  /**
   * Initiate login flow
   *
   * Redirects to Keycloak login page using Authorization Code Flow with PKCE.
   * On mobile, opens system browser. On web, redirects in same window.
   *
   * @param returnUrl in-app URL to come back to after the login (web only)
   */
  async login(returnUrl?: string): Promise<void> {
    // SECURITY FIX (MEDIUM-02): Rate limiting to prevent brute force/enumeration
    const now = Date.now();

    // Check minimum interval between attempts
    if (now - this.lastLoginAttempt < this.MIN_LOGIN_INTERVAL_MS) {
      const waitTime = Math.ceil((this.MIN_LOGIN_INTERVAL_MS - (now - this.lastLoginAttempt)) / 1000);
      throw new Error(`Please wait ${waitTime} seconds before trying to login again`);
    }

    // Check maximum attempts with cooldown
    if (this.loginAttempts >= this.MAX_LOGIN_ATTEMPTS) {
      const cooldownRemaining = Math.ceil((this.loginAttemptsCooldown - now) / 1000);
      if (now < this.loginAttemptsCooldown) {
        console.error('[Auth] Too many login attempts, cooldown active');
        throw new Error(`Too many login attempts. Please wait ${cooldownRemaining} seconds before trying again.`);
      }
      // Reset attempts after cooldown
      console.warn('[Auth] Cooldown expired, resetting login attempts');
      this.loginAttempts = 0;
    }

    // Update rate limiting counters
    this.lastLoginAttempt = now;
    this.loginAttempts++;

    // Set cooldown if max attempts reached
    if (this.loginAttempts >= this.MAX_LOGIN_ATTEMPTS) {
      this.loginAttemptsCooldown = now + this.COOLDOWN_PERIOD_MS;
    }

    try {
      if (this.platformService.isMobile()) {
        const authUrl = await this.buildAuthorizationUrl();
        await this.openAuthorizationUrl(authUrl);
      } else {
        // Web: the return URL rides in the OAuth state, which comes back with the callback
        // even in another tab — unlike _intendedUrl, which a full-page redirect erases.
        // handleCallback validates it on the way back, where it can have been tampered with.
        this.oauthService.initCodeFlow(returnUrl ?? '');
      }

    } catch (error) {
      console.error('[Auth] Login failed:', error);
      throw error;
    }
  }

  /**
   * Open the authorization URL on a mobile platform.
   *
   * iOS opens it in SAFARI, not an in-app browser. The login chain is
   *   our Keycloak -> Saviscio proxy (INTEROP_AUTH_PROXY_PROD) -> FAS -> itsme app
   * and when itsme is installed on the same device it finishes by opening an https
   * continuation URL. iOS always gives an https URL to the default browser, so the
   * return lands in Safari rather than in whatever in-app browser started the flow.
   *
   * Measured on device: with @capacitor/browser, FAS rejects the return outright;
   * with ASWebAuthenticationSession, FAS and itsme both succeed and then the Saviscio
   * proxy fails with "please login again through your application", because its
   * AUTH_SESSION_ID is a SESSION cookie and those are not shared with Safari.
   *
   * Starting in Safari puts that cookie in the jar the return arrives in. The final
   * redirect to myabvv://oauth-callback is then routed back to the app by URL scheme
   * and handled by DeepLinkService — the same path Android already uses.
   *
   * Android keeps the Browser flow: the itsme return re-enters the same Chrome
   * Custom Tab through an intent filter, so nothing is lost.
   *
   * If the native plugin is missing, fall back to Browser rather than leaving the
   * user with no way to log in at all.
   * @private
   */
  private async openAuthorizationUrl(authUrl: string): Promise<void> {
    if (!this.platformService.isiOS()) {
      await Browser.open({ url: authUrl });
      return;
    }

    try {
      await AuthSession.openInSystemBrowser({ url: authUrl });
    } catch (error) {
      if (isAuthSessionUnavailable(error)) {
        console.warn('[Auth] AuthSession plugin unavailable, falling back to Browser');
        await Browser.open({ url: authUrl });
        return;
      }
      throw error;
    }
  }

  /**
   * Login with test account credentials (for app store reviewers)
   * Uses Keycloak Direct Access Grant (ROPC) to authenticate directly
   * without browser redirect - single HTTP request to token endpoint.
   *
   * @param username Test account username (e.g., apple-tester, android-tester)
   * @param password Test account password
   */
  async loginWithTestAccount(username: string, password: string): Promise<void> {
    // SECURITY FIX: Apply same rate limiting as regular login to prevent brute force
    const now = Date.now();

    // Check minimum interval between attempts (500ms)
    if (now - this.lastLoginAttempt < this.MIN_LOGIN_INTERVAL_MS) {
      const waitTime = Math.ceil((this.MIN_LOGIN_INTERVAL_MS - (now - this.lastLoginAttempt)) / 1000);
      throw new Error(`Please wait ${waitTime} seconds before trying again`);
    }

    // Check maximum attempts with cooldown period
    if (this.loginAttempts >= this.MAX_LOGIN_ATTEMPTS) {
      const cooldownRemaining = Math.ceil((this.loginAttemptsCooldown - now) / 1000);
      if (now < this.loginAttemptsCooldown) {
        throw new Error(`Too many login attempts. Please wait ${cooldownRemaining} seconds`);
      }
      // Reset attempts after cooldown
      this.loginAttempts = 0;
    }

    // Update attempt tracking
    this.lastLoginAttempt = now;
    this.loginAttempts++;

    // Set cooldown if max attempts reached
    if (this.loginAttempts >= this.MAX_LOGIN_ATTEMPTS) {
      this.loginAttemptsCooldown = now + this.COOLDOWN_PERIOD_MS;
    }

    const tokenEndpoint = `${this.realmUrl}/protocol/openid-connect/token`;

    const body = new URLSearchParams({
      grant_type: 'password',
      client_id: this.config.keycloakContext,
      username: username,
      password: password,
      scope: 'openid profile email'
    });

    try {
      // Use CapacitorHttp to bypass CORS on mobile
      const options: HttpOptions = {
        url: tokenEndpoint,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        data: body.toString()
      };

      const response: HttpResponse = await CapacitorHttp.post(options);

      if (response.status !== 200) {
        let errorMsg = `HTTP ${response.status}`;
        try {
          const errorData = typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
          errorMsg = errorData?.error_description || errorData?.error || errorMsg;
        } catch { /* use default errorMsg */ }
        throw new Error(errorMsg);
      }

      // Parse token response
      let tokens: TokenResponse;
      if (typeof response.data === 'string') {
        tokens = JSON.parse(response.data);
      } else {
        tokens = response.data;
      }

      const accessToken = tokens.access_token || '';
      const refreshToken = tokens.refresh_token || '';
      const expiresAt = Date.now() + ((tokens.expires_in || 300) * 1000);
      const idToken = tokens.id_token || '';

      let grantedScopes = tokens.scope || 'openid profile email';
      if (Array.isArray(grantedScopes)) {
        grantedScopes = grantedScopes.join(' ');
      }

      // Store tokens in sessionStorage for angular-oauth2-oidc
      sessionStorage.setItem('access_token', accessToken);
      sessionStorage.setItem('id_token', idToken);
      if (refreshToken) {
        sessionStorage.setItem('refresh_token', refreshToken);
      }
      sessionStorage.setItem('expires_at', expiresAt.toString());
      sessionStorage.setItem('granted_scopes', grantedScopes);

      // Store ID token claims for oauthService.getIdentityClaims()
      const claims = this.decodeJwtPayload(idToken);
      if (claims) {
        sessionStorage.setItem('id_token_claims_obj', JSON.stringify(claims));
      }

      // Store in secure storage for mobile persistence
      await this.tokenStorage.setAccessToken(accessToken);
      await this.tokenStorage.setIdToken(idToken);
      if (refreshToken) {
        await this.tokenStorage.setRefreshToken(refreshToken);
      }
      await this.tokenStorage.setTokenExpiry(expiresAt);

      // Update auth state (parses claims, sets user profile, bridges to legacy)
      await this.updateAuthState();

      // Schedule proactive token refresh (oauthService doesn't manage ROPC tokens)
      this.scheduleTokenRefresh();

      // Navigate to home
      const intendedUrl = this.getIntendedUrl() || '/';
      this.clearIntendedUrl();
      await this.router.navigateByUrl(intendedUrl);

      this.isTestSession = true;
    } catch (error) {
      console.error('[AuthService] Test account login failed:', error);
      throw error;
    }
  }

  /**
   * Build authorization URL manually for mobile platforms
   * @private
   */
  private async buildAuthorizationUrl(): Promise<string> {
    const config = this.oauthService as OAuthService & { authorizationEndpoint?: string };
    const issuer = config.issuer || this.realmUrl;
    const authorizationEndpoint = config.authorizationEndpoint ||
      `${issuer}/protocol/openid-connect/auth`;

    // Generate PKCE challenge
    const state = this.generateRandomString(43);
    const codeVerifier = this.generateRandomString(43);
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    // Store PKCE parameters in Secure Storage
    await this.tokenStorage.set('pkce_code_verifier', codeVerifier);
    await this.tokenStorage.set('oauth_state', state);

    // Build URL with query parameters
    // prompt=login forces Keycloak to show the login page even if
    // the system browser has an active SSO session cookie
    const params = new URLSearchParams({
      client_id: this.config.keycloakContext,
      redirect_uri: this.platformService.getRedirectUri(),
      response_type: 'code',
      scope: 'openid profile email',
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      prompt: 'login'
    });

    return `${authorizationEndpoint}?${params.toString()}`;
  }

  /**
   * Generate random string for PKCE
   * @private
   */
  private generateRandomString(length: number): string {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);
    return Array.from(randomValues)
      .map(v => charset[v % charset.length])
      .join('');
  }

  /**
   * Generate PKCE code challenge from verifier
   * @private
   */
  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(new Uint8Array(hash));
  }

  /**
   * Base64 URL encode
   * @private
   */
  private base64UrlEncode(array: Uint8Array): string {
    const base64 = btoa(String.fromCharCode(...array));
    return base64
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  /**
   * Decode JWT payload and return claims object
   * @private
   */
  private decodeJwtPayload(token: string): IdTokenClaims | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64).split('').map(c =>
          '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        ).join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  /**
   * Extract SSIN from 'sub' claim (format: "f:uuid:ssin")
   * @private
   */
  private extractSsinFromSub(sub: string | undefined): string | undefined {
    if (!sub || typeof sub !== 'string') return undefined;
    const parts = sub.split(':');
    return parts.length >= 3 ? parts[parts.length - 1] : undefined;
  }

  /**
   * Handle authentication failure - clear state and redirect to error page
   * @private
   */
  private async handleAuthFailure(): Promise<void> {
    this._isAuthenticated$.next(false);
    this._userProfile$.next(null);

    // Clear tokens locally without redirecting to Keycloak
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('refresh_token');
    sessionStorage.removeItem('expires_at');
    sessionStorage.removeItem('granted_scopes');
    sessionStorage.removeItem('id_token_claims_obj');
    await this.tokenStorage.clearAll();

    if (this.platformService.isMobile()) {
      await this.router.navigateByUrl('/auth/landing');
    } else {
      window.location.href = '/assets/auth-failure.html';
    }
  }

  /**
   * Manually exchange authorization code for tokens
   * Used on mobile where we build the auth URL manually
   * @private
   */
  private async exchangeCodeForTokens(code: string, codeVerifier: string): Promise<void> {
    const tokenEndpoint = `${this.realmUrl}/protocol/openid-connect/token`;

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code: code,
      redirect_uri: this.platformService.getRedirectUri(),
      client_id: this.config.keycloakContext,
      code_verifier: codeVerifier
    });

    try {
      // Use CapacitorHttp to bypass CORS on mobile
      const options: HttpOptions = {
        url: tokenEndpoint,
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        data: body.toString()
      };

      const response: HttpResponse = await CapacitorHttp.post(options);

      if (response.status !== 200) {
        throw new Error(`Token exchange failed with status ${response.status}`);
      }

      // Parse response if needed
      let tokens: TokenResponse;
      if (typeof response.data === 'string') {
        tokens = JSON.parse(response.data);
      } else {
        tokens = response.data;
      }

      const accessToken = tokens.access_token || '';
      const refreshToken = tokens.refresh_token || '';
      const expiresAt = Date.now() + ((tokens.expires_in || 300) * 1000);

      // Ensure scope is a space-separated string
      let grantedScopes = tokens.scope || 'openid profile email';
      if (Array.isArray(grantedScopes)) {
        grantedScopes = grantedScopes.join(' ');
      }

      const idToken = tokens.id_token || '';

      // Store tokens in sessionStorage for angular-oauth2-oidc
      sessionStorage.setItem('access_token', accessToken);
      sessionStorage.setItem('id_token', idToken);
      if (refreshToken) {
        sessionStorage.setItem('refresh_token', refreshToken);
      }
      sessionStorage.setItem('expires_at', expiresAt.toString());
      sessionStorage.setItem('granted_scopes', grantedScopes);

      // Store ID token claims manually
      const claims = this.decodeJwtPayload(idToken);
      if (claims) {
        sessionStorage.setItem('id_token_claims_obj', JSON.stringify(claims));
      }

      // Store in secure storage for persistence
      await this.tokenStorage.setAccessToken(accessToken);
      await this.tokenStorage.setIdToken(idToken);
      if (refreshToken) {
        await this.tokenStorage.setRefreshToken(refreshToken);
      }
      await this.tokenStorage.setTokenExpiry(expiresAt);

      // Clean up PKCE parameters
      await this.tokenStorage.remove('pkce_code_verifier');
      await this.tokenStorage.remove('oauth_state');

    } catch (error) {
      console.error('[AuthService] Token exchange failed:', error);
      throw error;
    }
  }

  /**
   * Handle OAuth callback after successful authentication
   *
   * Processes the authorization code, exchanges it for tokens,
   * stores tokens securely, updates auth state, and navigates
   * to the intended URL or home.
   */
  async handleCallback(): Promise<void> {
    // Ensure OAuthService is configured before processing callback
    const tokenEndpoint = this.oauthService.tokenEndpoint;
    if (!tokenEndpoint) {
      this.configureOAuth();
      await this.oauthService.loadDiscoveryDocument();
    }

    // Read before tryLogin(): the library strips code/state from the URL.
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const oauthError = urlParams.get('error');
    const errorDescription = urlParams.get('error_description');

    try {
      // Check for OAuth error response
      if (oauthError) {
        throw new Error(`OAuth error: ${oauthError} - ${errorDescription || 'No description'}`);
      }

      // Verify we have a code
      if (!code) {
        throw new Error('No authorization code found in callback URL');
      }

      // For mobile, we need manual state/PKCE validation and token exchange
      // For web, angular-oauth2-oidc handles everything via tryLogin()
      if (this.platformService.isMobile()) {
        // Retrieve and validate state from Secure Storage
        const storedState = await this.tokenStorage.get('oauth_state');

        if (!storedState || state !== storedState) {
          await this.tokenStorage.remove('oauth_state');
          await this.tokenStorage.remove('pkce_code_verifier');
          throw new Error('Invalid state parameter - possible CSRF attack');
        }

        // Retrieve PKCE verifier from Secure Storage
        const codeVerifier = await this.tokenStorage.get('pkce_code_verifier');

        if (!codeVerifier) {
          await this.tokenStorage.remove('oauth_state');
          throw new Error('No PKCE code verifier - cannot complete token exchange');
        }

        await this.exchangeCodeForTokens(code!, codeVerifier!);
      } else {
        // Web: Let angular-oauth2-oidc handle state validation and token exchange
        const loginOptions = {
          preventClearHashAfterLogin: false
        };

        const loginResult = await this.oauthService.tryLogin(loginOptions);

        if (!loginResult) {
          throw new Error('Token exchange failed');
        }
      }

      if (this.oauthService.hasValidAccessToken()) {
        await this.storeTokens();

        // Clear PKCE parameters after successful token exchange
        await this.tokenStorage.remove('oauth_state');
        await this.tokenStorage.remove('pkce_code_verifier');

        await this.updateAuthState();

        // Proactive token refresh for web only (mobile: MobileTokenRefreshService handles it)
        if (!this.platformService.isMobile()) {
          this.scheduleTokenRefresh();
        }

        // Verify that user profile is loaded before navigation
        const currentProfile = this.getUser();

        // For external users (FAS), Belgian claims with SSIN are required
        // For internal users (LDAP), we just need a valid profile
        const isExternalUser = this.config.keycloakContext === 'myabvv-external';
        if (isExternalUser && !currentProfile?.belgianClaims?.ssin) {
          throw new Error('Failed to load user profile - cannot proceed with navigation');
        }

        if (!isExternalUser && !currentProfile) {
          throw new Error('Failed to load user profile - cannot proceed with navigation');
        }

        // Navigate to the URL carried in the OAuth state (it survives the redirect and a tab
        // switch), else the in-memory intended URL, else home
        const intendedUrl = safeReturnUrl(this.oauthService.state) ?? this.getIntendedUrl() ?? '/';
        this.clearIntendedUrl();

        await this.router.navigateByUrl(intendedUrl);
      } else {
        throw new Error('No valid access token after callback');
      }
    } catch (error) {
      console.error('[Auth] Callback processing failed:', error);
      window.location.href = '/assets/auth-failure.html';
    }
  }

  /**
   * Logout user
   *
   * On web: redirects to Keycloak logout page (standard OIDC flow).
   * On mobile: clears tokens locally and navigates to landing page.
   * Redirecting the WebView to Keycloak would break the app.
   */
  async logout(): Promise<void> {
    try {
      // Cancel proactive token refresh
      if (this.tokenRefreshTimer) {
        clearTimeout(this.tokenRefreshTimer);
        this.tokenRefreshTimer = null;
      }

      // Reset auth state
      this._isAuthenticated$.next(false);
      this._userProfile$.next(null);

      // Branches below must run before clearing storage: backchannelLogout()
      // reads refresh_token and oauthService.logOut() reads id_token (to send
      // as id_token_hint) from the same sessionStorage keys cleared after this.
      if (this.platformService.isMobile()) {
        // Mobile: terminate Keycloak session for real (FAS/itsme) sessions
        // Skip for ROPC test sessions (faster re-login during testing)
        if (!this.isTestSession) {
          await this.backchannelLogout();
        }
        this.isTestSession = false;
      } else if (this.isInternalUser()) {
        // Desktop internal (LAN): Kerberos/AD session, just clear and let re-auth happen
        this.oauthService.logOut();
      } else {
        // External web (DMZ): end the Keycloak session — the broker then logs out of FAS too —
        // and land on the public site. Only clearing the local tokens left the SSO session
        // valid, so the next visitor on this browser was silently logged in (MAF-674).
        const lang = sessionStorage.getItem('lang') || 'nl';
        this.oauthService.logOut({
          post_logout_redirect_uri: lang === 'fr'
            ? this.config.keycloakLogoutMembersFrUrl
            : this.config.keycloakLogoutMembersNlUrl
        });
      }

      // Clear secure storage (mobile persistence)
      await this.tokenStorage.clearAll();

      // Clear sessionStorage (angular-oauth2-oidc state)
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('id_token');
      sessionStorage.removeItem('refresh_token');
      sessionStorage.removeItem('expires_at');
      sessionStorage.removeItem('granted_scopes');
      sessionStorage.removeItem('id_token_claims_obj');

      if (this.platformService.isMobile()) {
        await this.router.navigateByUrl('/auth/landing');
      }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  /**
   * Refresh access token using refresh token
   *
   * @returns true if refresh succeeded, false otherwise
   */
  async refreshToken(): Promise<boolean> {
    try {
      await this.oauthService.refreshToken();
      await this.storeTokens();
      // Re-schedule next refresh after successful refresh
      this.scheduleTokenRefresh();
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  /**
   * Schedule proactive token refresh before the access token expires.
   * Used for ROPC-issued tokens where angular-oauth2-oidc's built-in
   * token_expires event doesn't fire (because it doesn't manage those tokens).
   * Refreshes 30 seconds before expiry, or at half the remaining lifetime.
   */
  private scheduleTokenRefresh(): void {
    // Clear any existing timer
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }

    const expiresAtStr = sessionStorage.getItem('expires_at');
    if (!expiresAtStr) return;

    const expiresAt = parseInt(expiresAtStr, 10);
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;

    if (timeUntilExpiry <= 0) return;

    // Refresh 30 seconds before expiry, but at least at half the remaining time
    const refreshIn = Math.max(timeUntilExpiry - 30000, timeUntilExpiry / 2);

    this.tokenRefreshTimer = setTimeout(async() => {
      const success = await this.refreshToken();
      if (!success) {
        console.error('[AuthService] Proactive token refresh failed');
      }
    }, refreshIn);
  }

  /**
   * Check if user is currently authenticated
   */
  isAuthenticated(): boolean {
    return this._isAuthenticated$.value;
  }

  /**
   * Get current user profile
   */
  getUser(): UserProfile | null {
    return this._userProfile$?.value || null;
  }

  /**
   * Check if access token is valid
   */
  hasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  /**
   * Get current ID token
   */
  getIdToken(): string | null {
    return this.oauthService.getIdToken();
  }

  /**
   * Get token expiration timestamp (milliseconds since epoch)
   */
  getTokenExpiration(): number | null {
    return this.oauthService.getAccessTokenExpiration();
  }

  /**
   * Store intended URL for post-login redirect
   */
  setIntendedUrl(url: string): void {
    this._intendedUrl = url;
  }

  /**
   * Get intended URL for post-login redirect
   */
  getIntendedUrl(): string | null {
    return this._intendedUrl;
  }

  /**
   * Clear intended URL
   */
  clearIntendedUrl(): void {
    this._intendedUrl = null;
  }

  /**
   * Get token expiry time
   * Returns Unix timestamp in milliseconds when the access token expires
   */
  async getTokenExpiry(): Promise<number | null> {
    return await this.tokenStorage.getTokenExpiry();
  }

  /**
   * Check if current user is internal user (MyABVV context vs myabvv-external)
   */
  isInternalUser(): boolean {
    return this.config.keycloakContext === 'MyABVV';
  }

  /**
   * Get user's SSIN (Belgian social security number) or internal user ID
   */
  getInss(): string | null {
    const profile = this.getUser();
    const ssin = profile?.belgianClaims?.ssin;

    // For internal users, return the identifier (could be employee ID, not SSIN)
    if (this.isInternalUser()) {
      return ssin || profile?.preferred_username || null;
    }

    // For external users, require valid SSIN (11 digits)
    if (!ssin) return null;
    const normalized = ssin.replace(/[-.]/g, '');
    return /^\d{11}$/.test(normalized) ? normalized : null;
  }

  /**
   * Get the display name of the currently authenticated user.
   * @returns Display name string or null if not available
   */
  getDisplayName(): string | null {
    const profile = this.getUser();
    // Try Belgian claims first, fall back to standard OIDC name claim
    return profile?.belgianClaims?.displayName || profile?.name || profile?.preferred_username || null;
  }

  getGivenName(): string | null {
    const profile = this.getUser();
    const givenName = profile?.['given_name'];
    if (typeof givenName === 'string' && givenName) return givenName.trim() || null;
    return this.getDisplayName();
  }

  /**
   * Configure OAuth2 settings for Keycloak
   * @private
   */
  private configureOAuth(): void {
    const authConfig: AuthConfig = {
      issuer: this.realmUrl,
      clientId: this.config.keycloakContext,
      redirectUri: this.platformService.getRedirectUri(),
      postLogoutRedirectUri: this.platformService.getPostLogoutRedirectUri(),
      responseType: 'code',
      scope: 'openid profile email',
      showDebugInformation: !this.config.production,
      requireHttps: this.config.production,
      useSilentRefresh: false,
      sessionChecksEnabled: false,
      tokenEndpoint: `${this.realmUrl}/protocol/openid-connect/token`,
      strictDiscoveryDocumentValidation: false
    };

    this.oauthService.configure(authConfig);
  }

  /**
   * Parse Belgian-specific claims from ID token
   *
   * Uses Belgian FAS custom claims (be:abvv-fgtb:*) when available,
   * and falls back to standard OIDC claims for missing values.
   *
   * @private
   */
  private parseBelgianClaims(claims: IdTokenClaims | null): BelgianClaims | undefined {
    if (!claims) return undefined;

    // Extract Belgian FAS custom claims (if available)
    const belgianSsin = claims['be:abvv-fgtb:ssin'];
    const belgianDisplayName = claims['be:abvv-fgtb:display_name'];
    const belgianPersonType = claims['be:abvv-fgtb:person_type'];
    const belgianUserId = claims['be:abvv-fgtb:user_id'];

    // If all custom claims are present, use them directly
    if (belgianSsin && belgianDisplayName && belgianPersonType && belgianUserId) {
      return { ssin: belgianSsin, displayName: belgianDisplayName, personType: belgianPersonType, userId: belgianUserId };
    }

    // Build claims by combining Belgian claims with standard OIDC claims fallbacks
    const ssin = belgianSsin || this.extractSsinFromSub(claims.sub) || claims.preferred_username;
    const personType = belgianPersonType || (this.isInternalUser() ? 'INTERNAL' : 'member');
    const userId = belgianUserId || ssin;

    // Display name: prefer Belgian claim, fallback to name or preferred_username
    let displayName = belgianDisplayName || claims.name || claims.preferred_username || '';
    if (displayName) {
      displayName = displayName.replace(/\s+/g, ' ').trim();
    }

    // For internal users, skip SSIN validation
    if (this.isInternalUser()) {
      return {
        ssin: ssin || claims.sub || '',
        displayName: displayName || '',
        personType,
        userId: userId || claims.sub || ''
      };
    }

    // For external users, validate SSIN format (11 digits)
    if (!ssin) return undefined;

    const normalizedSsin = ssin.replace(/[-.]/g, '');
    if (!/^\d{11}$/.test(normalizedSsin)) return undefined;

    return { ssin: normalizedSsin, displayName: displayName || '', personType, userId: userId || normalizedSsin };
  }

  /**
   * Store tokens in secure storage
   * @private
   */
  private async storeTokens(): Promise<void> {
    const accessToken = this.oauthService.getAccessToken();
    const idToken = this.oauthService.getIdToken();
    const refreshToken = this.oauthService.getRefreshToken();
    const expiration = this.oauthService.getAccessTokenExpiration();

    if (accessToken) {
      await this.tokenStorage.setAccessToken(accessToken);
    }

    if (idToken) {
      await this.tokenStorage.setIdToken(idToken);
    }

    if (refreshToken) {
      await this.tokenStorage.setRefreshToken(refreshToken);
    }

    if (expiration) {
      await this.tokenStorage.setTokenExpiry(expiration);
    }
  }

  /**
   * Load stored tokens from secure storage
   * @private
   */
  private async loadStoredTokens(): Promise<void> {
    const accessToken = await this.tokenStorage.getAccessToken();
    const idToken = await this.tokenStorage.getIdToken();
    const refreshToken = await this.tokenStorage.getRefreshToken();
    const tokenExpiry = await this.tokenStorage.getTokenExpiry();

    // Restore tokens to OAuthService
    if (accessToken && idToken) {
      sessionStorage.setItem('access_token', accessToken);
      sessionStorage.setItem('id_token', idToken);
      if (refreshToken) {
        sessionStorage.setItem('refresh_token', refreshToken);
      }
      if (tokenExpiry) {
        sessionStorage.setItem('expires_at', tokenExpiry.toString());
      }
      sessionStorage.setItem('granted_scopes', 'openid profile email');

      // Decode and restore ID token claims
      const claims = this.decodeJwtPayload(idToken);
      if (claims) {
        sessionStorage.setItem('id_token_claims_obj', JSON.stringify(claims));
      }

      // Restore user profile from secure storage
      const storedProfile = await this.tokenStorage.getUserProfile();

      if (storedProfile) {
        // Ensure belgianClaims is properly initialized (could be missing after storage)
        if (!storedProfile.belgianClaims && storedProfile.sub) {
          const ssin = storedProfile.preferred_username || this.extractSsinFromSub(storedProfile.sub) || '';
          storedProfile.belgianClaims = {
            ssin,
            displayName: storedProfile.name || storedProfile.preferred_username || '',
            personType: this.isInternalUser() ? 'INTERNAL' : 'member',
            userId: ssin
          };
        }

        this._userProfile$.next(storedProfile);
        this._isAuthenticated$.next(true);
        this.validateTokenIdentity(storedProfile, storedProfile);
      }
    }
  }

  /**
   * Setup token event listeners
   * @private
   */
  private setupTokenEventListeners(): void {
    // Listen for token received event
    this.oauthService.events
      .pipe(filter(e => e.type === 'token_received'))
      .subscribe({
        next: async() => {
          await this.storeTokens();
          await this.updateAuthState();
        },
        error: err => console.error('[AuthService] OAuth event error (token_received):', err)
      });

    // Listen for token refreshed event
    this.oauthService.events
      .pipe(filter(e => e.type === 'token_refreshed'))
      .subscribe({
        next: () => {
          this.storeTokens();
        },
        error: err => console.error('[AuthService] OAuth event error (token_refreshed):', err)
      });

    // Token expiration handling with auto-refresh
    this.oauthService.events
      .pipe(filter(e => e.type === 'token_expires'))
      .subscribe({
        next: async() => {
          if (this.platformService.isMobile()) {
            // Mobile: MobileTokenRefreshService handles token_expires exclusively
            return;
          }
          if (!this.isAuthenticated()) {
            return; // Guard against stale events after logout
          }
          const refreshed = await this.refreshToken();
          if (!refreshed) {
            console.warn('[AuthService] Token refresh failed on token_expires — TokenExpiryDialogComponent will handle it');
          }
        },
        error: err => console.error('[AuthService] OAuth event error (token_expires):', err)
      });

    // Listen for session terminated
    this.oauthService.events
      .pipe(filter(e => e.type === 'session_terminated'))
      .subscribe({
        next: () => {
          this._isAuthenticated$.next(false);
          this._userProfile$.next(null);
        },
        error: err => console.error('[AuthService] OAuth event error (session_terminated):', err)
      });
  }

  /**
   * Update authentication state from current tokens
   * @private
   */
  private async updateAuthState(): Promise<void> {
    const hasValidToken = this.oauthService.hasValidAccessToken();
    const currentProfile = this._userProfile$.value;

    // Don't clear profile if tokens exist in storage (timing issue workaround)
    if (!hasValidToken && currentProfile) {
      const accessToken = sessionStorage.getItem('access_token');
      const idToken = sessionStorage.getItem('id_token');

      if (accessToken && idToken) {
        return;
      }
    }

    this._isAuthenticated$.next(hasValidToken);

    if (hasValidToken) {
      const claims = this.oauthService.getIdentityClaims();

      // Prevent stale profiles from overwriting fresh ones (race condition fix)
      if (currentProfile && claims && claims['iat']) {
        const currentIat = currentProfile['iat'] || 0;
        const newIat = claims['iat'] || 0;

        if (newIat < currentIat) {
          return;
        } else if (newIat === currentIat && currentProfile['jti'] === claims['jti']) {
          return;
        }
      }

      const belgianClaims = this.parseBelgianClaims(claims);

      // Security validation for external users
      if (!this.isInternalUser()) {
        const hasValidBelgianClaims = belgianClaims?.ssin &&
          belgianClaims?.displayName &&
          belgianClaims?.personType &&
          /^\d{11}$/.test(belgianClaims.ssin);

        if (!hasValidBelgianClaims) {
          await this.handleAuthFailure();
          return;
        }
      }

      const userProfile: UserProfile = {
        sub: claims['sub'],
        email: claims['email'],
        name: claims['name'],
        preferred_username: claims['preferred_username'],
        belgianClaims: belgianClaims,
        ...claims
      };

      this._userProfile$.next(userProfile);
      await this.tokenStorage.setUserProfile(userProfile);
      this.validateTokenIdentity(userProfile, claims);

    } else {
      this._userProfile$.next(null);
    }
  }

  /**
   * SECURITY CONTROL. The only place checking the token's `azp` claim against the
   * configured client, and the only place enforcing that external callers are members.
   * These checks used to live inside bridgeToLegacyAuthService, whose stated job was
   * populating the legacy AuthenticatedUser — removing that lane would have deleted
   * them by accident. Throws on mismatch; callers must not swallow the error.
   * @private
   */
  private validateTokenIdentity(userProfile: UserProfile, claims: IdTokenClaims): void {
    const tokenClientId = claims['azp'];

    if (tokenClientId !== this.config.keycloakContext) {
      throw new Error('Token client ID mismatch - access denied');
    }
    if (tokenClientId === 'myabvv-external') {
      if (userProfile.belgianClaims?.personType !== 'member') {
        throw new Error('Invalid person type - access denied');
      }
    } else if (tokenClientId !== 'MyABVV') {
      throw new Error('Unknown client ID - access denied');
    }
  }
}
